import { MODULES } from "@/permissions/modules";

/**
 * Every surface in the app that a sellable module gates, and **where each one
 * is gated**.
 *
 * This array gates nothing itself. Gating happens in four places:
 *
 * 1. `module:` on a route object in `routes/router.jsx`
 * 2. `module:` on a menu item in `config/menu.js`
 * 3. `<ModuleGate module={...}>` around a component
 * 4. an empty served option list, for a form section whose options come from
 *    the API — the section renders only when the list is non-empty
 *
 * ## What is actually checked, and what cannot be
 *
 * `scripts/check-module-surfaces.mjs` runs on `npm run build` (via `prebuild`)
 * and fails the build on three things:
 *
 * - an entry whose `module` is not a real code — a typo gates nothing, silently;
 * - an entry whose `file` does not exist, or does not contain its `gate` string
 *   — this is the "an entry can name a surface that carries none" case;
 * - a `<ModuleGate>` or `module:` in `src/` that no entry accounts for — a real
 *   gate nobody wrote down here.
 *
 * **One thing no static check can see: a surface that is neither listed here
 * nor gated anywhere.** There is nothing to find — no entry, no gate, just a
 * button that renders for everybody. That is exactly how the Bulk Upload
 * controls on `/admin/job` shipped: `bulk_intake` had four entries, so the
 * old "every sellable module appears at least once" check passed while two
 * ungated buttons sat on a page nobody had listed. Only walking the app with
 * every module off finds those, which is the check that matters and the one a
 * person has to run.
 *
 * So this file is an inventory with teeth, not a proof of completeness. Add a
 * module to the backend registry, add its surfaces here, wire each one up, and
 * then walk the app.
 *
 * @typedef {object} ModuleSurface
 * @property {string} id     stable name for the surface
 * @property {string} module a value from MODULES
 * @property {string} kind   what it is: route, menu, tab, panel, button, …
 * @property {string} file   path under `frontend_v2/`, where the gate lives
 * @property {string} gate   exact text that must appear in `file`
 */

/** @type {ModuleSurface[]} */
export const MODULE_SURFACES = [
  // ai.boolean_search
  {
    id: "job-detail.boolean-tab-button",
    module: MODULES.AI_BOOLEAN_SEARCH,
    kind: "tab",
    file: "src/pages/admin/job-detail/index.jsx",
    gate: "useModuleAccess(MODULES.AI_BOOLEAN_SEARCH)",
  },
  {
    // `passthrough`: the tab is not entitled → nothing mounts and nothing
    // fetches; unavailable → the tab renders itself, because it owns the only
    // presentation that keeps generated strings readable.
    id: "job-detail.boolean-tab-body",
    module: MODULES.AI_BOOLEAN_SEARCH,
    kind: "panel",
    file: "src/pages/admin/job-detail/index.jsx",
    // check-module-surfaces.mjs parses this file with a regex that only matches
    // double-quoted values, so prettier's preferred single quotes here break the
    // build gate. prettier-ignore keeps the two from fighting over it.
    // prettier-ignore
    gate: "<ModuleGate module={MODULES.AI_BOOLEAN_SEARCH} variant=\"passthrough\">",
  },
  {
    id: "job-detail.boolean-tab-availability",
    module: MODULES.AI_BOOLEAN_SEARCH,
    kind: "notice",
    file: "src/pages/admin/job-detail/components/BooleanStringsTab.jsx",
    gate: "<ModuleUnavailableNotice module={MODULES.AI_BOOLEAN_SEARCH}",
  },
  {
    // No ModuleGate: the options come from `GET /job/field-config`, which
    // serves an empty list when the tenant is not entitled, and the section
    // renders only when the list is non-empty. That is the gate.
    id: "job-form.boolean-platform-selector",
    module: MODULES.AI_BOOLEAN_SEARCH,
    kind: "form-section",
    file: "src/components/admin/job/FieldConfigSelector.jsx",
    gate: "{booleanPlatforms.length > 0 && (",
  },

  // ai.interview_questions
  {
    id: "job-form.interview-type-selector",
    module: MODULES.AI_INTERVIEW_QUESTIONS,
    kind: "form-section",
    file: "src/components/admin/job/FieldConfigSelector.jsx",
    gate: "{interviewTypes.length > 0 && (",
  },
  {
    id: "candidate-analysis.interview-tab",
    module: MODULES.AI_INTERVIEW_QUESTIONS,
    kind: "tab",
    file: "src/pages/admin/candidate-analysis/constants/tabs.js",
    gate: "module: MODULES.AI_INTERVIEW_QUESTIONS",
  },
  {
    id: "candidate-analysis.interview-generate-controls",
    module: MODULES.AI_INTERVIEW_QUESTIONS,
    kind: "action",
    file: "src/pages/admin/candidate-analysis/interview/index.jsx",
    gate: "moduleEnabled &&",
  },

  // ai.candidate_job_fit
  {
    id: "candidate-analysis.job-fit-tab",
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    kind: "tab",
    file: "src/pages/admin/candidate-analysis/constants/tabs.js",
    gate: "module: MODULES.AI_CANDIDATE_JOB_FIT",
  },
  {
    id: "candidate-analysis.job-fit-analyze-control",
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    kind: "action",
    file: "src/pages/admin/candidate-analysis/job-fit/index.jsx",
    gate: "if (!moduleEnabled) return null;",
  },
  {
    // Both ways into Apply from Suitable Jobs. The action lives on the
    // candidate panel next to Download and Edit — none of which need the
    // module — so it is the one row there that has to be left out rather
    // than the page being gated.
    id: "candidate-analysis.apply-to-another-job-action",
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    kind: "action",
    file: "src/pages/admin/candidate-analysis/component/CandidateProfilePanel.jsx",
    gate: "useModuleAccess(MODULES.AI_CANDIDATE_JOB_FIT)",
  },
  {
    id: "route.apply-to-job-review",
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.AI_CANDIDATE_JOB_FIT",
  },
  // ai.jd_improver
  {
    // The chooser modal itself. With the module there are two ways to post a
    // job and the tenant picks one; without it there is one, so Post a New Job
    // opens the manual form and the chooser never appears.
    id: "job.post-modal-chooser",
    module: MODULES.AI_JD_IMPROVER,
    kind: "modal",
    file: "src/pages/admin/job/index.jsx",
    gate: "useModuleAccess(MODULES.AI_JD_IMPROVER)",
  },
  {
    // The "write it with AI" card in the post-job chooser, and the layout
    // around it — the lock behind the skip above, for any other way in.
    id: "job.post-modal-ai-card",
    module: MODULES.AI_JD_IMPROVER,
    kind: "button",
    file: "src/pages/admin/job/component/PostJobModal.jsx",
    gate: "<ModuleGate module={MODULES.AI_JD_IMPROVER}>",
  },
  {
    id: "job.ai-modal",
    module: MODULES.AI_JD_IMPROVER,
    kind: "modal",
    file: "src/pages/admin/job/index.jsx",
    // See the note on the boolean-search entry above.
    // prettier-ignore
    gate: "<ModuleGate module={MODULES.AI_JD_IMPROVER} variant=\"passthrough\">",
  },

  // bulk_intake
  {
    id: "job-detail.bulk-upload-button",
    module: MODULES.BULK_INTAKE,
    kind: "button",
    file: "src/pages/admin/job-detail/index.jsx",
    gate: "<ModuleGate module={MODULES.BULK_INTAKE}",
  },
  {
    // Two controls on the jobs list — the card button and the row-menu item.
    // Both shipped ungated: `bulk_intake` owns no permission code (the tenant
    // still needs candidate.create), so the PermissionGuard around them could
    // never hide them, and this file did not list the page at all.
    id: "job-list.bulk-upload-controls",
    module: MODULES.BULK_INTAKE,
    kind: "button",
    file: "src/pages/admin/job/component/JobCard.jsx",
    gate: "<ModuleGate module={MODULES.BULK_INTAKE}",
  },
  {
    id: "route.bulk-upload-and-review",
    module: MODULES.BULK_INTAKE,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.BULK_INTAKE",
  },
  // The four email-consent rows below are the one place a gate's module code is
  // not written in the file that applies it: `emailConsentFlows` on
  // GET /tenant-settings carries a `module` per flow, and two flows set one —
  // `bulk` ("bulk_intake") and `jobfit_apply` ("ai.candidate_job_fit"), the
  // same strings as the constants here. So the `gate` text is the prop that
  // passes the served code through, not the constant, and it is identical on
  // all four rows. A flow gaining a module server-side gates its row on both
  // screens with no change on this side; it still needs its own entry here, and
  // this comment is the reason why that is not obvious.
  {
    id: "org-settings.bulk-email-consent",
    module: MODULES.BULK_INTAKE,
    kind: "setting",
    file: "src/pages/admin/org-settings/index.jsx",
    gate: "module={flow.module}",
  },
  {
    id: "job-form.bulk-email-consent",
    module: MODULES.BULK_INTAKE,
    kind: "setting",
    file: "src/components/admin/job/ConsentConfigSelector.jsx",
    gate: "module={flow.module}",
  },
  {
    id: "org-settings.jobfit-apply-email-consent",
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    kind: "setting",
    file: "src/pages/admin/org-settings/index.jsx",
    gate: "module={flow.module}",
  },
  {
    id: "job-form.jobfit-apply-email-consent",
    module: MODULES.AI_CANDIDATE_JOB_FIT,
    kind: "setting",
    file: "src/components/admin/job/ConsentConfigSelector.jsx",
    gate: "module={flow.module}",
  },

  // compliance — one module, AI document extraction included. The extraction
  // summary on the detail page carries no gate of its own on purpose: it is a
  // read inside a route that already requires `compliance`.
  //
  // The route and menu entries below are ENTITLEMENT gates. They are not enough
  // on their own for a whole-page module: entitlement decides whether the page
  // exists, and availability decides whether its write controls work. A killed
  // Compliance used to render every Approve, Reject, Upload and Save live, and
  // each one answered 503 on click. So each page also carries an availability
  // gate, listed here beside its route.
  {
    id: "menu.compliance-section",
    module: MODULES.COMPLIANCE,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.COMPLIANCE",
  },
  {
    id: "route.compliance-pages",
    module: MODULES.COMPLIANCE,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.COMPLIANCE",
  },
  {
    id: "compliance.dashboard-availability",
    module: MODULES.COMPLIANCE,
    kind: "notice",
    file: "src/pages/admin/compliance/dashboard/index.jsx",
    gate: "<ModuleUnavailableNotice module={MODULES.COMPLIANCE}",
  },
  {
    id: "compliance.intake-availability",
    module: MODULES.COMPLIANCE,
    kind: "notice",
    file: "src/pages/admin/compliance/intake/index.jsx",
    gate: "<ModuleUnavailableNotice module={MODULES.COMPLIANCE}",
  },
  {
    // The audit log reads and exports; it writes nothing, so a kill switch
    // changes none of its behaviour. The notice is still owed: a reader
    // treating the trail as complete needs to know intake has stopped.
    id: "compliance.audit-log-availability",
    module: MODULES.COMPLIANCE,
    kind: "notice",
    file: "src/pages/admin/compliance/audit-log/index.jsx",
    gate: "<ModuleUnavailableNotice module={MODULES.COMPLIANCE}",
  },
  {
    id: "compliance.checklist-writes",
    module: MODULES.COMPLIANCE,
    kind: "action",
    file: "src/pages/admin/compliance/checklists/index.jsx",
    gate: "const writesBlocked = !isAvailable;",
  },
  {
    id: "compliance.checklist-builder-save",
    module: MODULES.COMPLIANCE,
    kind: "action",
    file: "src/pages/admin/compliance/checklists/components/ChecklistBuilderModal.jsx",
    gate: "useModuleAccess(MODULES.COMPLIANCE)",
  },
  {
    // Lives in Org Settings since Phase 9, not on the Checklists page. It is
    // the one compliance surface on a page the module does NOT own, so it
    // carries its own gates instead of inheriting the host page's: entitlement
    // via PermissionGuard (the card vanishes, rather than blanking the whole
    // settings screen), and the kill switch via its own notice + writesBlocked.
    id: "compliance.settings-entitlement",
    module: MODULES.COMPLIANCE,
    kind: "component",
    file: "src/pages/admin/org-settings/ComplianceSettingsCard.jsx",
    gate: "module={MODULES.COMPLIANCE}",
  },
  {
    id: "compliance.settings-save",
    module: MODULES.COMPLIANCE,
    kind: "action",
    file: "src/pages/admin/org-settings/ComplianceSettingsCard.jsx",
    gate: "const writesBlocked = !isAvailable;",
  },
  {
    id: "compliance.detail-review-writes",
    module: MODULES.COMPLIANCE,
    kind: "action",
    file: "src/pages/admin/compliance/detail/index.jsx",
    gate: "const writesBlocked = !complianceAvailable;",
  },
  {
    // Analytics writes nothing — every family and every CSV export is a GET, so
    // nothing here is disabled. The entry exists because the page still carries
    // the notice, and an unlisted gate fails the build.
    id: "compliance.analytics-availability",
    module: MODULES.COMPLIANCE,
    kind: "notice",
    file: "src/pages/admin/compliance/analytics/index.jsx",
    gate: "<ModuleUnavailableNotice module={MODULES.COMPLIANCE}",
  },
  {
    // A compliance write on a recruitment page, so it gets no cover from the
    // /compliance route gate. Permission alone misses it on both axes: a role
    // holding `*` matches `compliance.create` whatever entitlement filtering
    // removed, and no permission moves when the module is killed fleet-wide.
    // The same check also stops the page's GET /compliance/settings call.
    id: "candidate-list.send-to-compliance",
    module: MODULES.COMPLIANCE,
    kind: "button",
    file: "src/pages/admin/candidate/index.jsx",
    gate: "<ModuleGate module={MODULES.COMPLIANCE}>",
  },
  {
    // Connectivity Phase F. Compliance progress on the CANDIDATE page. The
    // module gate is what makes the whole tab vanish for an unentitled tenant
    // — button and mounted component both — so the tab's fetch never fires.
    //
    // As with job-detail, `tabs.js` is already a listed file, so the checker
    // would NOT have flagged this gate had it been left out. Written down on
    // purpose.
    id: "candidate-analysis.compliance-tab",
    module: MODULES.COMPLIANCE,
    kind: "tab",
    file: "src/pages/admin/candidate-analysis/constants/tabs.js",
    gate: "module: MODULES.COMPLIANCE",
  },
  {
    // Phase E follow-up. Send to Compliance from the job's own candidate
    // table, so a recruiter working a pipeline does not have to leave for the
    // candidate list to act. Same compliance write on a recruitment page as
    // the other two, and it gets no cover from the /compliance route gate.
    //
    // NOTE: `check-module-surfaces.mjs` skips a file once ANY entry names it,
    // so this gate would NOT have been flagged had it been left out —
    // job-detail/index.jsx is already listed. Written down deliberately.
    id: "job-detail.send-to-compliance",
    module: MODULES.COMPLIANCE,
    kind: "button",
    file: "src/pages/admin/job-detail/index.jsx",
    gate: "<ModuleGate module={MODULES.COMPLIANCE}>",
  },
  {
    // The tab the checklist panel now lives in. `isEnabled` (entitlement), not
    // `canUse`: an unentitled tenant loses the tab entirely and mounts nothing,
    // so the read never fires; a temporarily-killed module keeps it, because
    // the checklist the tenant already wrote must stay readable.
    //
    // As with the send button below, `job-detail/index.jsx` is already a listed
    // file, so the checker would NOT have flagged this gate had it been left
    // out. Written down deliberately.
    id: "job-detail.compliance-checklist-tab",
    module: MODULES.COMPLIANCE,
    kind: "tab",
    file: "src/pages/admin/job-detail/index.jsx",
    gate: "useModuleAccess(MODULES.COMPLIANCE)",
  },
  {
    // Phase D. Which checklist a job's candidates default to, and what it asks
    // for. A read-only panel, so `variant="passthrough"`: a killed module
    // leaves it showing, because the default it names is still true and it has
    // no action to withdraw. Now the body of the Compliance Checklist tab
    // above, rather than a card over the tab bar — the gate is unchanged, but
    // the tab means it no longer mounts on every visit to the page.
    id: "job-detail.compliance-checklist-card",
    module: MODULES.COMPLIANCE,
    kind: "panel",
    file: "src/pages/admin/job-detail/components/JobComplianceChecklistCard.jsx",
    gate: "<ModuleGate module={MODULES.COMPLIANCE}",
  },
  {
    // The same send, from the candidate profile. It was a read-only badge
    // and needed no entry; Connectivity Phase B gave it a button, which is
    // what makes it a surface. `canUse` rather than `isAvailable`: a killed
    // module leaves the badge rendering — a recruiter still needs to know
    // why the status bar will not move — and withdraws only the action.
    id: "candidate-profile.send-to-compliance",
    module: MODULES.COMPLIANCE,
    kind: "action",
    file: "src/pages/admin/candidate-analysis/component/ComplianceIndicator.jsx",
    gate: "useModuleAccess(MODULES.COMPLIANCE)",
  },

  // branding — same split: the route withholds the page, and the page itself
  // disables Save, Publish, Discard, Reset and every asset control while the
  // module is dark. The published branding keeps serving either way.
  {
    id: "menu.branding",
    module: MODULES.BRANDING,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.BRANDING",
  },
  {
    id: "route.branding",
    module: MODULES.BRANDING,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.BRANDING",
  },
  {
    id: "branding.write-controls",
    module: MODULES.BRANDING,
    kind: "action",
    file: "src/pages/admin/branding/index.jsx",
    gate: "const writesBlocked = !isAvailable;",
  },

  // feedback — the tenant side: the header button that opens the form, and
  // the "My Feedback" page with its menu link. The SuperAdmin queue is not
  // module-gated, so it has no entry.
  {
    id: "header.feedback-button",
    module: MODULES.FEEDBACK,
    kind: "button",
    file: "src/components/feedback/FeedbackButton.jsx",
    gate: "<ModuleGate module={MODULES.FEEDBACK}>",
  },
  {
    id: "menu.my-feedback",
    module: MODULES.FEEDBACK,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.FEEDBACK",
  },
  {
    id: "route.my-feedback",
    module: MODULES.FEEDBACK,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.FEEDBACK",
  },

  // interview_scheduling — same split as branding/compliance: the route
  // withholds the page from a tenant not entitled, and the page itself keeps
  // rendering existing candidates/interviews while disabling Schedule,
  // Reschedule and Cancel during a fleet-wide kill.
  {
    id: "menu.interview-scheduling",
    module: MODULES.INTERVIEW_SCHEDULING,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.INTERVIEW_SCHEDULING",
  },
  {
    id: "route.interview-scheduling",
    module: MODULES.INTERVIEW_SCHEDULING,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.INTERVIEW_SCHEDULING",
  },
  {
    id: "interview-scheduling.write-controls",
    module: MODULES.INTERVIEW_SCHEDULING,
    kind: "action",
    file: "src/pages/admin/interview-scheduling/index.jsx",
    gate: "useModuleAvailability(MODULES.INTERVIEW_SCHEDULING)",
  },
  {
    // The "open this candidate's chat" action on every Interview Scheduling
    // row. Gated on OUTREACH rather than INTERVIEW_SCHEDULING on purpose: it
    // navigates into the Communication Center, so it is the destination's
    // entitlement that decides whether offering it is honest. A tenant with
    // interviews but no outreach would otherwise be shown a button that lands
    // on Unauthorized.
    id: "interview-scheduling.candidate-chat",
    module: MODULES.OUTREACH,
    kind: "action",
    file: "src/pages/admin/interview-scheduling/components/CandidateChatButton.jsx",
    gate: "ModuleGate module={MODULES.OUTREACH}",
  },
  {
    id: "communications.schedule-interview-button",
    module: MODULES.INTERVIEW_SCHEDULING,
    kind: "button",
    file: "src/components/interview-scheduling/ScheduleFromChatButton.jsx",
    gate: "<ModuleGate module={MODULES.INTERVIEW_SCHEDULING}>",
  },

  // resume_template — CV formatting. The module says "does this tenant have
  // CV formatting at all"; template grants (a separate, ungated axis — the
  // server filters `/list` to what is granted, and an empty list is a normal
  // response, not an error) say "which of the 17 designs".
  {
    id: "candidate-profile.export-formatted-cv-button",
    module: MODULES.RESUME_TEMPLATE,
    kind: "button",
    file: "src/pages/admin/candidate-analysis/component/CandidateProfilePanel.jsx",
    // Imperative, not `<ModuleGate>`: this item lives in a `Select`-built
    // Actions menu, which has no way to wrap one option's JSX. The option is
    // left out of the array entirely when `!canUse` — see the comment above
    // `resumeTemplateAccess` in that file.
    gate: "useModuleAccess(MODULES.RESUME_TEMPLATE)",
  },
  {
    id: "resume-template-modal.availability",
    module: MODULES.RESUME_TEMPLATE,
    kind: "notice",
    file: "src/pages/admin/candidate-analysis/component/ResumeTemplateModal.jsx",
    gate: "<ModuleUnavailableNotice",
  },
  {
    id: "resume-template-modal.download-blocked",
    module: MODULES.RESUME_TEMPLATE,
    kind: "action",
    file: "src/pages/admin/candidate-analysis/component/ResumeTemplateModal.jsx",
    gate: "const exportBlocked = !isAvailable;",
  },

  // No `guardian_settings` entry. It stopped being a module on 2026-08-10 — the
  // prompt-safety policy says everything it said and more — so the org-settings
  // guardian card is ungated on purpose. Every tenant has it; the policy decides
  // whether they own the switch.

  // outreach — outreach settings (templates, tone, channels), sequences, and
  // the communication center (its Chat view and its Table view, the latter
  // being what /admin/inbox now redirects to).
  // Entitlement (route + menu) covers the feature area existing at
  // all; permission codes still gate the RBAC side underneath, unchanged.
  {
    id: "menu.outreach-section",
    module: MODULES.OUTREACH,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.OUTREACH",
  },
  {
    id: "route.outreach-pages",
    module: MODULES.OUTREACH,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.OUTREACH",
  },

  // outreach.email / outreach.chat_channels — the two channel-group children.
  // WhatsApp and Telegram are sold together; Email is separate. Neither has a
  // route of its own — both gate a channel picker's option list or a settings
  // card, so entitlement/availability are checked from inside the component.
  {
    id: "outreach-settings.telegram-channel-card",
    module: MODULES.OUTREACH_CHAT,
    kind: "panel",
    file: "src/pages/admin/outreach-settings/components/ChannelsPanel.jsx",
    gate: "<ModuleGate module={MODULES.OUTREACH_CHAT}>",
  },
  {
    id: "templates.channel-picker-email",
    module: MODULES.OUTREACH_EMAIL,
    kind: "form-section",
    file: "src/pages/admin/outreach-settings/components/MessageTemplatesPanel.jsx",
    gate: "useModuleAccess(MODULES.OUTREACH_EMAIL)",
  },
  {
    id: "templates.channel-picker-chat",
    module: MODULES.OUTREACH_CHAT,
    kind: "form-section",
    file: "src/pages/admin/outreach-settings/components/MessageTemplatesPanel.jsx",
    gate: "useModuleAccess(MODULES.OUTREACH_CHAT)",
  },
  {
    // The composer's Email chip and Send: an organisation without email sees
    // neither, rather than a live Send the route refuses with 403.
    id: "communications.composer-email",
    module: MODULES.OUTREACH_EMAIL,
    kind: "form-section",
    file: "src/pages/admin/communications/index.jsx",
    gate: "useModuleAccess(MODULES.OUTREACH_EMAIL)",
  },
  {
    // The WhatsApp and Telegram chips, Send on them, and the Telegram invite.
    id: "communications.composer-chat",
    module: MODULES.OUTREACH_CHAT,
    kind: "form-section",
    file: "src/pages/admin/communications/index.jsx",
    gate: "useModuleAccess(MODULES.OUTREACH_CHAT)",
  },
  {
    id: "candidate-analysis.email-tab",
    module: MODULES.OUTREACH_EMAIL,
    kind: "tab",
    file: "src/pages/admin/candidate-analysis/constants/tabs.js",
    gate: "module: MODULES.OUTREACH_EMAIL",
  },
  {
    // Step 2 of Send to Compliance — the editable email that carries the
    // candidate's portal link. It sends through outreach, so without the
    // module there is no step 2: the modal keeps its one-step "Send to
    // Compliance" and its copy-link panel instead (Q-NoOutreach). Not hidden,
    // swapped — a compliance-only tenant must still be able to send someone.
    id: "compliance.send-email-step",
    module: MODULES.OUTREACH_EMAIL,
    kind: "form-section",
    file: "src/pages/admin/candidate/components/SendToComplianceModal.jsx",
    gate: "useModuleAccess(MODULES.OUTREACH_EMAIL)",
  },
  // sequences.step-channel-picker-email / -chat removed: StepEditor.jsx no
  // longer renders a per-step Channel field (i1789) — the sequence's channel
  // shows once via ChannelBadge in the step header instead, which needs no
  // module gate of its own.

  // sourcing — campaigns, shortlist and the tenant's own API credentials.
  // The three pages plus the three menu items; the Configure APIs page carries
  // a second, independent gate inside it (the SuperAdmin's bring-your-own-keys
  // switch), which is why it appears here AND explains itself on screen.
  {
    id: "route.campaigns-list",
    module: MODULES.SOURCING,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.SOURCING",
  },
  {
    id: "menu.sourcing-section",
    module: MODULES.SOURCING,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.SOURCING",
  },

  // extension — the install/download page and its menu item. The API half is
  // gated in extension/dependencies.py rather than on the router include,
  // because /extension/download and /extension/version are unauthenticated.
  {
    id: "route.chrome-extension",
    module: MODULES.EXTENSION,
    kind: "route",
    file: "src/routes/router.jsx",
    gate: "module: MODULES.EXTENSION",
  },
  {
    id: "menu.browser-extension",
    module: MODULES.EXTENSION,
    kind: "menu",
    file: "src/config/menu.js",
    gate: "module: MODULES.EXTENSION",
  },
];

/**
 * Modules with no surface of their own, and why.
 *
 * A sellable module belongs in `MODULE_SURFACES`. These are the always-on
 * ones: nothing can withhold them, so nothing needs hiding.
 */
export const ALWAYS_ON = new Set([MODULES.AI_RESUME_EXTRACTION, MODULES.AI_RESUME_REUPLOAD, MODULES.AI_CANDIDATE_MATCH]);

const KNOWN_CODES = new Set(Object.values(MODULES));

/**
 * Development-time warning for the one thing this file can check at runtime:
 * a module code with no entry, and an entry naming a code that does not exist.
 *
 * The file/gate half is checked at build time instead, by
 * `scripts/check-module-surfaces.mjs`, which can read the source this cannot.
 *
 * Deliberately **not** named "assert every module has a surface". It used to
 * be, and the name oversold it: `bulk_intake` had four entries while two of
 * its buttons were ungated, and the check passed. An entry proves someone
 * wrote a line here, nothing more.
 *
 * `console.error` rather than a thrown error: a wrong entry must be impossible
 * to miss, and must never take the app down for a user.
 */
export const warnOnUnmappedModules = () => {
  const covered = new Set();

  for (const surface of MODULE_SURFACES) {
    if (!KNOWN_CODES.has(surface.module)) {
      console.error(`[moduleSurfaces] "${surface.id}" names an unknown module code: ${surface.module}`);
      continue;
    }
    covered.add(surface.module);
  }

  const uncovered = Object.values(MODULES).filter((code) => !ALWAYS_ON.has(code) && !covered.has(code));

  if (uncovered.length > 0) {
    console.error(
      `[moduleSurfaces] These modules have no surface listed: ${uncovered.join(", ")}. ` +
        "A tenant without one will call its endpoint and be told 403 about a feature they never bought.",
    );
  }
};

/**
 * Development-only drift check against the served registry.
 *
 * Call it with the payload of `GET /platform/modules/registry` when a
 * SuperAdmin page has already fetched it. **Never adds a request of its own,
 * and never runs for a tenant user** — the catalogue is SuperAdmin-only, and
 * a check is not a reason to widen who can see it.
 *
 * The failure it catches is a module added on the server and never added
 * here: the client would keep rendering its surface for tenants who do not
 * own it, and they would meet a 403 instead.
 */
export const warnOnRegistryDrift = (registryModules) => {
  if (!import.meta.env.DEV || !Array.isArray(registryModules)) return;

  const served = new Set(registryModules.map((m) => m.code));
  const held = new Set(Object.values(MODULES));

  const missingHere = [...served].filter((code) => !held.has(code));
  const staleHere = [...held].filter((code) => !served.has(code));

  if (missingHere.length > 0) {
    console.warn(`[modules] The server has modules this client does not know: ${missingHere.join(", ")}. Add them to MODULES and gate their surfaces.`);
  }
  if (staleHere.length > 0) {
    console.warn(`[modules] This client holds module codes the server no longer has: ${staleHere.join(", ")}.`);
  }
};

export default MODULE_SURFACES;
